import { useState, useRef, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GREETING = {
  en: "Hi! I'm Brunchi's assistant.",
  es: "Hola! Soy el asistente de Brunchi. Preguntame sobre vinos, cerveza, licores, cestas regalo o cosas que hacer en Playa Blanca!",
  fr: "Bonjour! Je suis l'assistant Brunchi. Demandez-moi tout sur le vin, la biere, les spiritueux ou les choses a faire a Playa Blanca!",
  de: "Hallo! Ich bin Brunchis Assistent. Fragen Sie mich alles uber Wein, Bier, Spirituosen oder Aktivitaten in Playa Blanca!",
  it: "Ciao! Sono l'assistente di Brunchi. Chiedimi tutto su vino, birra, liquori o cose da fare a Playa Blanca!",
};

const QUICK_QUESTIONS = [
  { label: "Wine Tasting", msg: "Tell me about the wine tasting" },
  { label: "Yaiza Wine", msg: "What is Yaiza wine?" },
  { label: "Playa Blanca", msg: "What to do in Playa Blanca?" },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: GREETING.en },
  ]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => `brunchi-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const sendMessage = async (text) => {
    if (!text.trim() || streaming) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setStreaming(true);

    const assistantMsg = { role: 'assistant', text: '' };
    setMessages(prev => [...prev, assistantMsg]);

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text.trim(), session_id: sessionId }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') break;
            fullText += data;
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', text: fullText };
              return updated;
            });
          }
        }
      }
    } catch {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'assistant',
          text: 'Sorry, connection issue. Please email us at consultas@brunchi.shop!'
        };
        return updated;
      });
    }
    setStreaming(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (!open) {
    return (
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-5 z-[60] bg-amber-700 hover:bg-amber-800 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-xl transition-all hover:scale-110"
        aria-label="Chat with Brunchi"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </button>
    );
  }

  return (
    <div data-testid="chat-widget-panel" className="fixed bottom-20 right-5 z-[60] w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-8rem)] bg-stone-900 border border-stone-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-amber-800 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white text-sm font-bold">B</div>
          <div>
            <div className="text-white font-semibold text-sm">Brunchi Assistant</div>
            <div className="text-amber-200 text-xs">Wine, Beer & Spirits Expert</div>
          </div>
        </div>
        <button
          data-testid="chat-widget-close"
          onClick={() => setOpen(false)}
          className="text-white/70 hover:text-white transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollbarWidth: 'thin' }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              data-testid={`chat-message-${i}`}
              className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-amber-700 text-white rounded-br-sm'
                  : 'bg-stone-800 text-stone-200 rounded-bl-sm border border-stone-700'
              }`}
            >
              {msg.text}
              {streaming && i === messages.length - 1 && msg.role === 'assistant' && (
                <span className="inline-block w-1.5 h-4 bg-amber-500 ml-0.5 animate-pulse" />
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Questions (only show if < 3 messages) */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1.5 shrink-0">
          {QUICK_QUESTIONS.map((q, i) => (
            <button
              key={i}
              data-testid={`chat-quick-${i}`}
              onClick={() => sendMessage(q.msg)}
              disabled={streaming}
              className="text-xs bg-stone-800 text-amber-300 border border-stone-700 px-2.5 py-1 rounded-full hover:bg-stone-700 transition-colors disabled:opacity-50"
            >
              {q.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="px-3 py-2.5 border-t border-stone-700 flex gap-2 shrink-0 bg-stone-900">
        <input
          ref={inputRef}
          data-testid="chat-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about wine, beer, Playa Blanca..."
          disabled={streaming}
          className="flex-1 bg-stone-800 text-white text-sm border border-stone-600 rounded-lg px-3 py-2 placeholder-stone-500 focus:outline-none focus:border-amber-600 disabled:opacity-50"
        />
        <button
          data-testid="chat-send-btn"
          type="submit"
          disabled={streaming || !input.trim()}
          className="bg-amber-700 hover:bg-amber-600 text-white rounded-lg px-3 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </form>
    </div>
  );
}
